import React from 'react';
import { connect } from 'react-redux';
import { AppState } from '../store/state';
import { AppDispatch } from '../store/store';
import { Conference, Announcement, Role, Track } from '../store/conference/types';
import {
  selectCurrentConference,
  selectCurrentRole,
  selectCurrentTrack,
  selectCurrentUser,
  selectAssignmentState,
  selectSubmissionState,
  selectConference,
  selectKeywordState,
  selectInfo,
  selectTable,
  selectCanViewAuthors,
} from '../store/selectors';
import Loading from '../components/Loading/Loading';
import { Submission } from '../store/submission/types';
import { Answer, Answerable, Form, Question, ValidationStatus } from '../store/form/types';
import { FormTypeEnum } from '../store/form/types.d';
import { Link } from '../components/ui';
import { Review, viewModeType } from '../store/review/types';
import EditIcon from '../icons/Edit';
import { getFormsData } from '../helpers/form';
import SubmissionCard from '../components/SubmissionCard/SubmissionCard';
import MyGridRedux from '../components/MyGridRedux/MyGridRedux';
import { AssignmentPermissionManager, can } from '../helpers/permissions';
import styled from 'styled-components';
import history from '../store/history';
import { fillRoutePath } from '../helpers/path';
import { getRouteByName } from '../router/routes';
import { RowAction } from '../components/types';
import AnnouncementSidePanel from '../components/AnnouncementSidePanel/AnnouncementSidePanel';
import assignmentSlice from '../store/review/slice';
import ViewNewIcon from '../icons/ViewNew';
import useTheme from '@material-ui/core/styles/useTheme';
import { useTranslation } from 'react-i18next';
import { getValidationStatusName } from '../helpers/translations';
import { InfoGetDTO, TableFriendlyName } from '../store/info/types';
import infoSlice from '../store/info/slice';
import tableSlice from '../store/table/slice';
import { CustomColumnsDataGet } from '../store/table/types';
import { getSubmissionRow } from '../helpers/table';
import EditSubmission from '../icons/EditSubmission';
import ViewModeSelector from '../components/ViewModeSelector/ViewModeSelector';
import { User } from '../store/user/types';
import useFetchTableData from '../store/table/useFetchTableData';

interface Props {
  loading: boolean;
  conference: Conference | null;
  track: Track | null;
  role: Role | null;
  submissionsById: { [key: string]: Submission };
  user: User;
  assignments: Review[];
  canViewAuthors: boolean;
  announcementById: { [key: string]: Announcement };
  viewMode: viewModeType;
  changeViewModeAction: (viewMode: viewModeType) => void;
  keywordById: { [key: number]: Keyword };
}

const ReviewsPage: React.FC<Props> = ({
  loading,
  conference,
  track,
  role,
  submissionsById,
  user,
  assignments,
  canViewAuthors,
  announcementById,
  viewMode,
  changeViewModeAction,
  keywordById,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const friendlyName: TableFriendlyName = 'submissions_to_review';
  const [fetchData, fetchCustomColumnsData] = useFetchTableData(friendlyName);

  // const [dialog, setDialog] = useState<JSX.Element | undefined>(undefined);

  const isSummarizedView = can('ACCESS_SUMMARIZED_VIEWS');

  if (!conference || !track || !role || loading) {
    return <Loading />;
  }

  const getActions = (submission: Submission): RowAction[] => {
    const actions: RowAction[] = [];

    const callback = () => {
      history.push(
        fillRoutePath(getRouteByName('RouteDetailSubmission').path, {
          id: submission.external_id.toString(),
        }),
      );
    };
    actions.push({
      id: 'view-submission',
      label: t('View submission'),
      callback: callback,
      element: (
        <Link className="cursor-pointer text-sm flex items-center" onClick={callback}>
          <ViewNewIcon />
          <span className="ml-1">{t('View submission')}</span>
        </Link>
      ),
    });

    /*    const spm = new SubmissionPermissionManager(submission);

    if (spm.canEditPaperStatus(role)) {
      const callback = () =>
        setDialog(
          <PaperStatusChangeDialog
            initialOption={submissionsById[submission.id].paper_status_id as unknown as string}
            submissionIds={[submission.id]}
            open={true}
            handleClose={() => setDialog(undefined)}
            role={role}
          />,
        );
      actions.push({
        id: 'edit-paper-status',
        label: t('Edit paper status'),
        callback: callback,
        element: (
          <Link key={actions.length} className="flex items-center cursor-pointer" onClick={callback}>
            <EditIcon color={theme.palette.primary.main} className="mr-1" />
            {t('Edit paper status')}
          </Link>
        ),
      });
    }

    if (spm.canViewDiscussion(user, role)) {
      const callback = () => {
        history.push(
          fillRoutePath(getRouteByName('RouteDiscussions').path, {
            submissionId: submission.external_id.toString(),
          }),
        );
      };
      actions.push({
        id: 'join-discussion',
        label: t('Join discussion'),
        callback: callback,
        element: (
          <Link className="cursor-pointer text-sm flex items-center" onClick={callback}>
            <ViewNewIcon />
            <span className="ml-1">{t('Join discussion')}</span>
          </Link>
        ),
      });
    }*/

    return actions;
  };

  const userAssignments: {
    [key: number]: {
      answerable: Answerable;
      formsData: { form: Form; questions: Question[]; answers: Answer[] }[];
    };
  } = {};
  assignments
    .filter((assignment) => new AssignmentPermissionManager(assignment).isAuthor(user.person.id, role.id))
    .map((assignment) =>
      getFormsData(FormTypeEnum.Review, ['read', 'write'], role.id, null, user.person.id, assignment),
    )
    .forEach((value) => {
      const assignment = value.answerable as Review;
      userAssignments[assignment.submission_id] = value;
    });

  const reviewActions: { [key: string]: RowAction[] } = {};
  const inputData: { [key: string]: Submission } = {};
  Object.values(submissionsById).forEach((submission) => {
    let validationStatus: ValidationStatus | undefined = undefined;
    const actions: RowAction[] = viewMode == 'cards' ? getActions(submission) : [];
    if (userAssignments[submission.id]) {
      const assignment = userAssignments[submission.id].answerable as Review;
      const formsData = userAssignments[submission.id].formsData;
      const canEdit = new AssignmentPermissionManager(assignment).canEdit(
        user.person.id,
        role,
        formsData.map(({ form }) => form),
      );
      switch (viewMode) {
        case 'cards':
          // In cards view validation status is only shown when state is pending.
          validationStatus = assignment.validation_status === 'Pending' ? assignment.validation_status : undefined;
          break;
        case 'table':
          // In table view validation status is always shown
          validationStatus = assignment.validation_status;
          break;
      }
      if (canEdit) {
        // Push edit review action
        const callback = () => {
          history.push(
            fillRoutePath(getRouteByName('RouteEditReview').path, {
              submissionId: submission.external_id.toString(),
              reviewId: assignment.external_id.toString(),
              roleId: assignment.role_id.toString(),
            }),
          );
        };
        actions.push({
          id: 'edit-review',
          label: t('Edit review'),
          callback: callback,
          element: (
            <Link key={actions.length} className="flex items-center cursor-pointer" onClick={callback}>
              <EditIcon color={theme.palette.primary.main} className="mr-1" />
              {t('Edit review')}
            </Link>
          ),
        });
      }
    }

    const newRegister = {
      ...getSubmissionRow(submission, canViewAuthors, keywordById, t),
      validation_status: validationStatus,
    };

    // @ts-ignore
    inputData[submission.id] = newRegister;
    reviewActions[submission.id] = actions;
  });

  const announcement = announcementById[role.id];

  /* Decide what to display */
  let element;
  if (!Object.keys(inputData).length) {
    element = (
      <>
        <div className="h-full flex justify-center items-center">
          <StyledMessageWrapper className="flex justify-center items-center flex-col mb-6">
            <StyledIcon>
              <EditSubmission />
            </StyledIcon>
            <h2 className="text-xl font-bold w-max mt-3">{t('No papers to review yet')}</h2>
            <p className="mt-3 mb-6 text-center">
              {t(
                'You still have papers to review. Once you have assigned papers, you will be able to start the review process.',
              )}
            </p>
          </StyledMessageWrapper>
        </div>
      </>
    );
  } else {
    element = (
      <>
        <div className="mb-6 flex justify-between">
          <h2 className="font-bold text-xl mb-4 mr-5">{t('Submissions to review')}</h2>
          <ViewModeSelector viewMode={viewMode} handleClick={changeViewModeAction} />
        </div>
        {viewMode === 'table' ? (
          <MyGridRedux
            friendlyName="submissions_to_review"
            inputData={Object.values(inputData).map((value) => {
              return {
                ...value,
                validation_status: value.validation_status
                  ? getValidationStatusName(value.validation_status, t)
                  : value.validation_status,
              };
            })}
            initRowActions={reviewActions}
            getInfoAction={fetchData}
            getCustomColumnsData={fetchCustomColumnsData}
            // TODO: enable checkboxes in summarized views in the future
            selectable={!isSummarizedView}
          />
        ) : (
          <StyledCardsWrapper className="flex justify-start flex-wrap grow">
            {Object.values(inputData).map((submission, index) => {
              return (
                <div key={index} className="mb-6 w-80 mobileCtrlReviewsPageCardWrapper">
                  <SubmissionCard
                    submission={submission}
                    actions={reviewActions[submission.id]}
                    validationStatus={
                      submission.validation_status
                        ? {
                            title: t('Review status'),
                            status: submission.validation_status,
                          }
                        : undefined
                    }
                    canViewAuthors={canViewAuthors}
                    authorConfirmation={track.author_confirmation}
                    personId={user.person.id}
                    role={role}
                  />
                </div>
              );
            })}
          </StyledCardsWrapper>
        )}
      </>
    );
  }

  return (
    <div className="flex h-full mobileCtrlReviewsPageContainerWrapper">
      <StyledContainer className="flex flex-col overflow-y-auto overflow-x-hidden mr-2 mobileCtrlReviewsPageContainer">
        {element}
      </StyledContainer>

      {announcement && <AnnouncementSidePanel conference={conference} announcement={announcement} />}

      {/* dialog */}
    </div>
  );
};

const mapStateToProps = (state: AppState) => ({
  loading: selectSubmissionState(state).loading || selectAssignmentState(state).loading || selectInfo(state).loading,
  conference: selectCurrentConference(state),
  track: selectCurrentTrack(state),
  role: selectCurrentRole(state),
  submissionsById: selectSubmissionState(state).submissionsById,
  user: selectCurrentUser(state),
  assignments: selectAssignmentState(state).assignments,
  canViewAuthors: selectCanViewAuthors(state),
  announcementById: selectConference(state).announcementById,
  viewMode: selectAssignmentState(state).viewMode,
  keywordById: selectKeywordState(state).keywordById,
  customColumnsData: selectTable(state).customColumnsData,
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  changeViewModeAction: (viewMode: viewModeType) => dispatch(assignmentSlice.actions.CHANGE_VIEWMODE(viewMode)),
  getInfoAction: (data: InfoGetDTO) => dispatch(infoSlice.actions.GET(data)),
  getCustomColumnsDataAction: (data: CustomColumnsDataGet) =>
    dispatch(tableSlice.actions['CUSTOMCOLUMNS:DATA:GET'](data)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ReviewsPage);

const StyledCardsWrapper = styled.div`
  margin-left: -0.5rem;
  margin-right: -0.5rem;
`;

const StyledContainer = styled.div`
  flex-grow: 1;

  .custom-cols-4 {
    grid-template-columns: repeat(4, minmax(0, 25%));
  }

  .custom-cols-5 {
    grid-template-columns: repeat(5, minmax(0, 20%));
  }
`;

const StyledMessageWrapper = styled.div`
  max-width: 30rem;

  h2 {
    font-size: 1.45rem;
  }
  p {
    font-size: 1.1rem;
    line-height: 1.5rem;
  }
`;

const StyledIcon = styled.div`
  svg {
    width: 7.5rem;
    height: 7.5rem;
  }
`;
